import { consola } from 'consola'
import { ref, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { pickDeliveryCoords } from '../lib/deliveryCoords'
import { useModalStore , modalStateEnum } from '@shared/model'
import { useCartStore } from '@entities/order'
import { useInstitutionStore, apiInstitutions } from '@entities/institution'
import { useUserProfileStore , apiProfileInstance, formatSelectedDeliveryAddress } from '@entities/user'
import { useRulesForForm , convertUtcToMoscowTime , getPlatform , useAnalytics , getUtmParams } from '@shared/lib'
import { usePaymentStore , useRetryPayment , useOrderStorage } from '~/features/payment/@x/basket'



import { getDeliveryCostByRules } from '@shared/config'


import type { CreateOrderInput, OrderItemInput } from '@entities/order'


interface useCheckoutForm {
  textTitleMessage: string
  form: any
}

/**
 * Повторные нажатия «Оплатить» после ошибки валидации — та же попытка оформления.
 * Без окна каждый клик давал бы отдельный checkout_start и завышал счёт событий.
 */
const CHECKOUT_START_THROTTLE_MS = 60 * 1000
let lastCheckoutStartAt = 0

export function useCheckoutForm({ textTitleMessage: _textTitleMessage, form: _form }: useCheckoutForm) {
  const storeModal = useModalStore()
  const cartStore = useCartStore()
  const storeInstitution = useInstitutionStore()
  const storeUserProfile = useUserProfileStore()
  const checkoutFormStore = useCheckoutFormStore()
  const paymentStore = usePaymentStore()
  const { track } = useAnalytics()

  const { RulesForFormName, RulesForFormPhone, RulesForFormMethodReceipt, RulesForFormDeliveryAddress } = useRulesForForm()

  // Состояния
  const loader = ref(false)
  const errorMessage = ref<string | null>(null)
  const successMessage = ref<string | null>(null)
  const isSubmitted = ref(false)
  const confirmationToken = ref<string | null>(null)
  const currentOrderId = ref<number | null>(null)

  // Правила валидации (ключевой момент для "обязательного адреса" при delivery)
  const rules = computed(() => ({
    name: RulesForFormName(),
    phone: RulesForFormPhone(),
    method_receipt: RulesForFormMethodReceipt,
    payment_method: { required: true },
    delivery_address: checkoutFormStore.getForm.method_receipt === 'delivery'
      ? RulesForFormDeliveryAddress()
      : {},
  }))

  const v$ = useVuelidate(rules, checkoutFormStore.OrderForm)

  const deliveryCost = computed(() => {
    const institution = storeInstitution.institution
    const deliveryRules = institution?.delivery_rules

    consola.debug('💰 [deliveryCost computed] Calculating delivery cost:', {
      totalPrice: cartStore.totalPrice,
      hasInstitution: !!institution,
      hasDeliveryRules: !!deliveryRules,
      deliveryRulesType: Array.isArray(deliveryRules) ? 'array' : typeof deliveryRules,
      deliveryRulesLength: Array.isArray(deliveryRules) ? deliveryRules.length : 'N/A',
    })

    return getDeliveryCostByRules(cartStore.totalPrice, deliveryRules)
  })

  const paymentOptions = [
    { value: 'card', label: 'Картой онлайн' },
    { value: 'cash', label: 'Наличными' },
    { value: 'card_courier', label: 'Картой курьеру' },
  ]

  /**
   * Координаты адреса доставки — по ним сервер авторитетно подбирает филиал (см.
   * pickDeliveryCoords). Точка филиала координатами клиента не считается.
   */
  const resolveDeliveryCoords = () =>
    pickDeliveryCoords(
      storeUserProfile.selectedDeliveryAddress,
      checkoutFormStore.deliveryCoords,
      storeInstitution.branches,
    )

  /**
   * Ошибки сервера про адрес доставки, которые чинятся только на карте: координат нет,
   * в строке нет номера дома, адрес вне зон. Сообщение показывает OrderVerification,
   * а карту открываем сами — без лишнего шага «найди, где поменять адрес».
   */
  const ADDRESS_MAP_ERROR_CODES = new Set([
    'DELIVERY_COORDS_REQUIRED',
    'DELIVERY_ADDRESS_INCOMPLETE',
    'DELIVERY_ADDRESS_UNCONFIRMED',
    'DELIVERY_OUT_OF_ZONE',
  ])

  /**
   * Коды, при которых сервер забраковал саму ТОЧКУ (координаты филиала, центр
   * населённого пункта, адрес не подтверждается рядом с точкой). Такую точку надо
   * снять с выбранного адреса, иначе гость закроет карту и получит тот же отказ:
   * отличить испорченную запись от настоящей на клиенте нечем.
   *
   * DELIVERY_OUT_OF_ZONE сюда НЕ входит: там точка корректна, просто адрес не
   * обслуживается — снимать координаты не за что.
   */
  const STALE_POINT_ERROR_CODES = new Set([
    'DELIVERY_COORDS_REQUIRED',
    'DELIVERY_ADDRESS_INCOMPLETE',
    'DELIVERY_ADDRESS_UNCONFIRMED',
  ])

  /**
   * Заметки заказа: комментарий к заказу из корзины и комментарий курьеру из адреса.
   *
   * Комментарий курьеру («зелёный забор, вторые ворота») клиент пишет в окне карты, и до
   * 16.09.2026 он оседал только в сохранённом адресе: в заказ не попадал, а значит его
   * не видели ни касса, ни оператор в админке — его читал лишь курьер Яндекса, которому
   * он уходит отдельным полем заявки. Для своих курьеров, а это большинство зон, поле
   * было чёрной дырой. Теперь он едет в заметках заказа с пометкой «Курьеру», чтобы в
   * кассе было видно, кому адресована фраза.
   *
   * Только для доставки: на самовывозе курьера нет, а комментарий мог остаться в
   * выбранном адресе с прошлого заказа.
   */
  const buildOrderNotes = (): string => {
    const orderComment = checkoutFormStore.getForm.comment?.trim() || ''
    const isDelivery = checkoutFormStore.getForm.method_receipt === 'delivery'
    const courierComment = isDelivery
      ? storeUserProfile.selectedDeliveryAddress?.comment?.trim() || ''
      : ''

    // Клиент мог написать одно и то же в оба поля — второй раз не повторяем.
    const courierPart =
      courierComment && courierComment !== orderComment ? `Курьеру: ${courierComment}` : ''

    return [courierPart, orderComment].filter(Boolean).join('; ')
  }

  /**
   * Метки рекламы для заказа. Undefined, если рекламной атрибуции нет — пустой объект
   * заводил бы в отчёте фантомную кампанию «не указана» на каждом органическом заказе.
   */
  const buildOrderUtm = (): CreateOrderInput['utm'] => {
    const utm = getUtmParams()
    if (!utm.utmSource) return undefined

    return {
      source: utm.utmSource,
      medium: utm.utmMedium,
      campaign: utm.utmCampaign,
      content: utm.utmContent,
      term: utm.utmTerm,
    }
  }

  // Добавляем composable для работы с повторной оплатой
  useRetryPayment()

  // Добавляем useOrderStorage для работы с историей заказов
  const orderStorage = useOrderStorage()

  // eslint-disable-next-line sonarjs/cognitive-complexity -- оформление заказа и сценарии платёжных систем
  const submitForm = async () => {
    consola.debug('🎯 [submitForm] Starting order submission...')
    consola.debug('📋 [submitForm] Form data:', checkoutFormStore.getForm)

    // Отмечаем попытку до всех проверок: отвал на приостановленной оплате или на
    // валидации — тоже часть воронки, и без этого шага он выглядел бы как обрыв.
    // Повторные клики после ошибки валидации — та же попытка, не считаем их заново.
    if (Date.now() - lastCheckoutStartAt > CHECKOUT_START_THROTTLE_MS) {
      lastCheckoutStartAt = Date.now()
      track('checkout_start', {
        itemsCount: cartStore.cartItems?.length ?? 0,
        sum: cartStore.totalPrice ?? 0,
      })
    }

    // Защита: приём оплаты/заказов приостановлен в админке («Не выбран»). Кнопка
    // уже заблокирована (isOrderDisabled), это страховка от обхода + фолбэка active_payment_system.
    if (storeInstitution.institution?.payment_paused) {
      consola.warn('⛔ [submitForm] Оплата временно приостановлена — оформление отменено')
      return
    }

    // Перед оплатой: выбрана доставка, но адрес ещё не задан — пробуем молча подставить
    // избранный (дефолтный) адрес из профиля и синхронизировать форму, чтобы валидация
    // прошла. Если избранного адреса нет — валидация ниже остановит оформление и
    // подсветит поле адреса (пользователь укажет его на карте).
    if (
      checkoutFormStore.getForm.method_receipt === 'delivery' &&
      !checkoutFormStore.getForm.delivery_address?.address
    ) {
      const resolved = await storeUserProfile.ensureDefaultDeliveryAddress()
      const selected = storeUserProfile.selectedDeliveryAddress
      if (resolved && selected) {
        checkoutFormStore.setForm({
          delivery_address: {
            address: selected.address,
            zona: selected.zona == null ? undefined : String(selected.zona),
          },
          delivery_zone_id: selected.zona == null ? null : String(selected.zona),
        })
      }
    }

    v$.value.$validate()
    if (v$.value.$error) {
      consola.error('❌ [submitForm] Validation errors:', v$.value.$errors)
      return
    }

    consola.debug('✅ [submitForm] Validation passed')

    localStorage.removeItem('currentOrder')
    paymentStore.resetPayment()
    loader.value = true

    try {
      const notes = buildOrderNotes()
      const activePaymentSystem = storeInstitution.institution?.active_payment_system || 'tbusiness'

      consola.debug('🏦 [submitForm] Payment system:', activePaymentSystem)

      // Готовим items для заказа (OrderItemDto)
      const items: OrderItemInput[] = cartStore.cart.map((item: any) => {
        // Проверяем тип элемента (новая структура API)
        if (item.itemType === 'combo') {
          // Комбо товар
          return {
            itemType: 'combo',
            comboId: String(item.comboId),
            quantity: item.quantity,
            comboSelections: item.comboSelections || [],
          };
        } else if (item.itemType === 'product') {
          // Обычный товар (новая структура).
          // ⚠️ Поля называются ИМЕННО addons/excludes — так их читает бэкенд
          // (CreateOrderDto.OrderItemDto + order.controller.ts → addonIds/excludeIngredientIds).
          // Раньше тут слали addonIds/excludeIngredientIds — этих полей в DTO нет, ValidationPipe
          // их срезал, и добавки/исключения товара молча терялись (сумма расходилась с бэком).
          return {
            itemType: 'product',
            productId: String(item.productId),
            quantity: item.quantity,
            addons: item.addons && item.addons.length > 0 ? item.addons.map(String) : undefined,
            excludes: item.excludes && item.excludes.length > 0 ? item.excludes.map(String) : undefined,
          };
        } else {
          // Легаси формат (для обратной совместимости)
          return {
            productId: String(item.id),
            quantity: item.quantity,
            addons: item.addons && item.addons.length > 0 ? item.addons.map(String) : undefined,
            excludes: item.excludes && item.excludes.length > 0 ? item.excludes.map(String) : undefined,
          };
        }
      })

      // Определяем methodReceipt
      let methodReceipt: 'pickup' | 'delivery' | 'dine_in'
      if (checkoutFormStore.getForm.method_receipt === 'delivery') {
        methodReceipt = 'delivery'
      } else if (checkoutFormStore.getForm.method_receipt === 'dine_in') {
        methodReceipt = 'dine_in'
      } else {
        methodReceipt = 'pickup'
      }

      // Определяем нужно ли инициировать платеж (только для онлайн оплаты)
      const isOnlinePayment = checkoutFormStore.getForm.payment_method === 'card' ||
                               checkoutFormStore.getForm.payment_method === 'bank_card'

      const deliveryCoords = resolveDeliveryCoords()

      // Создаем payload для API V2 (CreateOrderDto)
      // Бэкенд сам рассчитает все суммы и может сразу инициировать платеж
      const orderPayload: CreateOrderInput = {
        userId: String(storeUserProfile.getUser?.id),
        branchId: String(storeInstitution.selectedBranch?.id),
        institutionId: String(storeInstitution.institution?.id),
        orderType: checkoutFormStore.getForm.method_receipt,
        paymentMethod: checkoutFormStore.getForm.payment_method === 'card' ? 'bank_card' : checkoutFormStore.getForm.payment_method,
        clientPlatform: import.meta.client ? getPlatform() : 'web',
        // Реклама, приведшая к заказу. Метки живут в атрибуции 30 суток, поэтому сюда
        // попадёт кампания, даже если человек пришёл по объявлению неделю назад.
        utm: buildOrderUtm(),
        paymentProvider: activePaymentSystem as 'yookassa' | 'alfabank' | 'tbusiness',
        initiatePayment: isOnlinePayment, // Автоматически инициировать платеж для онлайн оплаты
        useWidget: true, // Использовать встроенный виджет оплаты
        items,
        pointsToUse: checkoutFormStore.getForm.pointsToUse || undefined,
        promocodeId: checkoutFormStore.getForm.promocodeId || undefined,
        // Единый формат строки заказа: «населённый пункт, улица, дом, подъезд N, кв. M» —
        // точка из формы (или выбранного адреса) плюс уточнения выбранного адреса.
        deliveryAddress: checkoutFormStore.getForm.method_receipt === 'delivery'
          ? formatSelectedDeliveryAddress(
              storeUserProfile.selectedDeliveryAddress,
              checkoutFormStore.getForm.delivery_address?.address
                || storeUserProfile.selectedDeliveryAddress?.address,
            ) || undefined
          : undefined,
        deliveryZoneId: checkoutFormStore.getForm.method_receipt === 'delivery'
          ? (checkoutFormStore.getForm.delivery_zone_id && checkoutFormStore.getForm.delivery_zone_id !== '' && checkoutFormStore.getForm.delivery_zone_id !== null
          ? String(checkoutFormStore.getForm.delivery_zone_id)
              : (storeUserProfile.selectedDeliveryAddress?.zona == null
                  ? undefined
                  : String(storeUserProfile.selectedDeliveryAddress.zona)))
          : undefined,
        // Координаты адреса доставки — см. deliveryCoords выше.
        deliveryLatitude: checkoutFormStore.getForm.method_receipt === 'delivery'
          ? deliveryCoords?.latitude
          : undefined,
        deliveryLongitude: checkoutFormStore.getForm.method_receipt === 'delivery'
          ? deliveryCoords?.longitude
          : undefined,
        methodReceipt,
        tableId: checkoutFormStore.getForm.table_id ? String(checkoutFormStore.getForm.table_id) : undefined,
        personCount: checkoutFormStore.getForm.cutlery || undefined,
        notes: notes || undefined,
        reservationTime: checkoutFormStore.getForm.preorder_mode === 'scheduled' && checkoutFormStore.getForm.reservation_time
          ? checkoutFormStore.getForm.reservation_time
          : undefined,
      }

      consola.debug('📦 [submitForm] Order payload prepared (CreateOrderDto):', {
        userId: orderPayload.userId,
        branchId: orderPayload.branchId,
        institutionId: orderPayload.institutionId,
        orderType: orderPayload.orderType,
        paymentMethod: orderPayload.paymentMethod,
        clientPlatform: orderPayload.clientPlatform,
        paymentProvider: orderPayload.paymentProvider,
        initiatePayment: orderPayload.initiatePayment,
        useWidget: orderPayload.useWidget,
        itemsCount: orderPayload.items.length,
        methodReceipt: orderPayload.methodReceipt,
        deliveryAddress: orderPayload.deliveryAddress,
        deliveryZoneId: orderPayload.deliveryZoneId,
        promocodeId: orderPayload.promocodeId,
        pointsToUse: orderPayload.pointsToUse,
        tableId: orderPayload.tableId,
        personCount: orderPayload.personCount,
        hasNotes: !!orderPayload.notes,
      })

      consola.debug('📦 [submitForm] Full order payload (JSON):', JSON.stringify(orderPayload, null, 2))

      // Создаем заказ
      consola.debug('🚀 [submitForm] Sending order to API...')
      const response = await apiProfileInstance.createOrder(orderPayload)
      consola.debug('✅ [submitForm] API response received:', response)

        // 1. Создание заказа завершено
        if (response.success && response.order?.id) {
          consola.debug('✅ [submitForm] Order created successfully:', {
            orderId: response.order.id,
            status: response.order.status,
            paymentStatus: response.order.paymentStatus,
            totalAmount: response.order.totalAmount,
            hasPaymentInfo: !!response.payment,
          })

          const orderId = Number(response.order.id)

          // Преобразуем время в московское время перед сохранением
          const orderWithMoscowTime = {
            ...response.order,
            createdAt: convertUtcToMoscowTime(response.order.createdAt),
            updatedAt: convertUtcToMoscowTime(response.order.updatedAt),
          }

          consola.debug('🕐 [submitForm] Time conversion:', {
            originalUTC: response.order.createdAt,
            moscowTime: orderWithMoscowTime.createdAt,
          })

          paymentStore.setOrder(orderWithMoscowTime)

          // Сохраняем заказ в localStorage для отслеживания
          localStorage.setItem('currentOrder', JSON.stringify(orderWithMoscowTime))
          consola.debug('💾 [submitForm] Order saved to localStorage with Moscow time')

          // Сохраняем заказ в историю пользователя
          orderStorage.saveOrderToHistory(response.order)
          consola.debug('📚 [submitForm] Order saved to user history')

          currentOrderId.value = orderId

          // Заказ создан. Оплаченным он станет позже — по вебхуку провайдера,
          // поэтому здесь фиксируется именно создание, а не деньги.
          track('checkout_submit', {
            orderId: Number(orderId),
            sum: Number(response.order.totalAmount ?? cartStore.totalPrice ?? 0),
            itemsCount: cartStore.cartItems?.length ?? 0,
            deliveryType: checkoutFormStore.getForm.method_receipt,
          })

          // 2. ✅ ОПТИМИЗИРОВАНО: Добавляем краткие данные заказа в Store
          consola.debug('🔄 [submitForm] Adding order summary to unpaid orders store...')
          try {
            const { useUnpaidOrdersStore } = await import('~/features/payment/@x/basket')
            const unpaidOrdersStore = useUnpaidOrdersStore()

            // ✅ Добавляем ТОЛЬКО краткие данные (OrderSummary)
            unpaidOrdersStore.addSummary({
              id: String(orderId),
              paymentStatus: response.order.paymentStatus,
              externalPaymentId: response.order.externalPaymentId,
              status: response.order.status,
              createdAt: orderWithMoscowTime.createdAt,
              updatedAt: orderWithMoscowTime.updatedAt,
            })

            consola.debug('✅ [submitForm] Order summary added to store:', orderId)
          } catch (error) {
            consola.error('❌ [submitForm] Failed to add order summary to store:', error)
          }

        // Ошибка на этапе открытия оплаты: показываем её ПОСЛЕ сброса сообщений ниже,
        // иначе сброс затрёт текст и пользователь не поймёт, почему ничего не открылось.
        let paymentErrorMessage: string | null = null

        // 3. Обработка платежа (если был инициирован)
        if (response.payment) {
          consola.debug('💳 [submitForm] Payment was initiated by backend')

          // Виджет провайдера открывается — дальше пользователь уходит из нашего UI,
          // и исход оплаты принесёт вебхук, а не этот код.
          track('checkout_payment_method_select', {
            method: String(response.payment.provider ?? checkoutFormStore.getForm.payment_method ?? 'unknown'),
          })
          consola.debug('📊 [submitForm] Payment provider:', response.payment.provider)
          consola.debug('📊 [submitForm] Available providers:', response.payment.availableProviders)
          consola.debug('🔍 [submitForm] Full payment info:', JSON.stringify(response.payment, null, 2))

          // Сохраняем общие данные платежа
          paymentStore.setOrderId(orderId)
          if (response.order.totalAmount) {
            paymentStore.setAmount(response.order.totalAmount)
          }

          // Закрываем модальное окно корзины
          storeModal.close()

          // Открываем виджет в зависимости от провайдера
          consola.debug('🎨 [submitForm] Opening payment widget for provider:', response.payment.provider)
          switch (response.payment.provider) {
            case 'yookassa': {
              if (response.payment.confirmationToken) {
                consola.debug('🏦 [submitForm] YooKassa widget with token')
                confirmationToken.value = response.payment.confirmationToken
                paymentStore.setConfirmationToken(response.payment.confirmationToken)
                storeModal.open(modalStateEnum.YooKassaWidget)
              } else if (response.payment.paymentUrl) {
                consola.debug('🏦 [submitForm] YooKassa direct redirect')
                paymentStore.setConfirmationUrl(response.payment.paymentUrl)
                window.open(response.payment.paymentUrl, '_blank')
              }
              break
            }

            case 'alfabank': {
              // Альфа-Банк работает по схеме «платёжная страница на стороне банка»:
              // карточные данные вводятся у банка, мы лишь уводим клиента на paymentUrl (formUrl).
              // URL возврата задаёт бэкенд (payment_success_url заведения) — подставлять их здесь нельзя.
              const paymentUrl = response.payment.paymentUrl
              consola.debug('🏦 [submitForm] AlfaBank payment URL:', paymentUrl)
              if (paymentUrl) {
                paymentStore.setConfirmationUrl(paymentUrl)
                const alfaBankData = {
                  confirmationUrl: paymentUrl,
                  paymentId: response.payment.paymentId || '',
                  isTest: Boolean(response.payment.isTest),
                  orderId: String(orderId),
                }
                consola.debug('📦 [submitForm] AlfaBank widget data:', alfaBankData)
                storeModal.open(modalStateEnum.AlfaBankWidget, alfaBankData)
              } else {
                consola.error('❌ [submitForm] NO PAYMENT URL RECEIVED FROM BACKEND!')
                paymentErrorMessage = 'Сервер не вернул ссылку для оплаты. Попробуйте ещё раз или выберите другой способ оплаты.'
              }
              break
            }

            case 'sberbank': {
              const paymentUrl = response.payment.paymentUrl
              consola.debug('🏦 [submitForm] Sberbank payment:', paymentUrl)
              if (paymentUrl) {
                paymentStore.setConfirmationUrl(paymentUrl)
                window.open(paymentUrl, '_blank')
              }
              break
            }

            default: {
              const paymentUrl = response.payment.paymentUrl
              consola.debug('🏦 [submitForm] TBusiness payment:', paymentUrl)
              if (paymentUrl) {
                paymentStore.setConfirmationUrl(paymentUrl)
                storeModal.open(modalStateEnum.TBusinessWidget)
              }
              break
            }
          }

          consola.debug('✅ [submitForm] Payment widget opened successfully')
        } else if (isOnlinePayment) {
          // Заказ создан, но бэкенд не смог инициировать платёж (нет настроек провайдера,
          // ошибка шлюза и т.п.). Раньше корзина просто молча закрывалась — для пользователя
          // это выглядело как «нажал оплатить, и ничего не открылось».
          // Заказ есть, оплаты нет — самый дорогой вид отвала: корзина уже собрана.
          track('payment_result', {
            orderId: Number(orderId),
            ok: false,
            errorCode: response.paymentError?.code ?? 'not_initiated',
          })

          consola.error('❌ [submitForm] Payment was not initiated by backend:', response.paymentError)
          paymentErrorMessage = response.paymentError?.message
            ? `Не удалось перейти к оплате: ${response.paymentError.message}. Заказ №${orderId} сохранён — оплатите его в разделе «Мои заказы».`
            : `Не удалось перейти к оплате. Заказ №${orderId} сохранён — оплатите его в разделе «Мои заказы».`
        } else {
          consola.debug('ℹ️ [submitForm] No payment initiated (cash or other offline payment method)')
          // Для оффлайн оплаты просто закрываем корзину
          storeModal.close()
        }

        successMessage.value = null
        errorMessage.value = paymentErrorMessage

        // Очищаем поле комментария после успешного создания заказа
        checkoutFormStore.setField('comment', '')

        consola.debug('✅ [submitForm] Order submission completed successfully')

      } else {
        consola.error('❌ [submitForm] Order creation failed:', response)
        errorMessage.value = 'Ошибка при создании заказа'
        // Очищаем поле комментария при ошибке создания заказа
        checkoutFormStore.setField('comment', '')
      }
    } catch (error: any) {
      consola.error('❌ [submitForm] Error during order submission:', error)
      consola.error('❌ [submitForm] Error details:', {
        message: error?.message,
        response: error?.response,
        data: error?.data,
        stack: error?.stack,
      })

      // ✅ Извлекаем понятное сообщение с сервера
      const serverMessage = error?.response?._data?.message || error?.data?.message
      errorMessage.value = serverMessage || error?.message || 'Произошла ошибка при отправке заказа'

      // Гонка авто-роутинга: филиал перегрузился между подбором и оформлением.
      // Повторно подбираем филиал по сохранённым координатам и просим повторить
      // (без авто-ресабмита — платёжный поток не дублируем).
      const serverCode = error?.response?._data?.code || error?.data?.code
      if (ADDRESS_MAP_ERROR_CODES.has(serverCode)) {
        if (STALE_POINT_ERROR_CODES.has(serverCode)) {
          checkoutFormStore.setDeliveryCoords(null)
          await storeUserProfile.dropSelectedAddressCoords()
        }
        storeModal.open(modalStateEnum.ModalMapYandex, { from: 'checkout', orderType: 'delivery' })
      }
      if (serverCode === 'DELIVERY_CAPACITY_EXCEEDED') {
        const coords = resolveDeliveryCoords()
        const instId = storeInstitution.institution?.id
        if (coords && instId) {
          try {
            // Состав корзины: пере-подбор тоже выбирает только филиал, который
            // делает ВЕСЬ заказ (см. cart-aware резолв на бэке).
            const productIds = cartStore.cart
              .filter((i: any) => i.itemType !== 'combo')
              .map((i: any) => String(i.productId ?? i.id))
            const comboIds = cartStore.cart
              .filter((i: any) => i.itemType === 'combo')
              .map((i: any) => String(i.comboId))
              .filter((id: string) => id && id !== 'undefined')
            const { data } = await apiInstitutions.resolveDeliveryBranch(Number(instId), {
              latitude: coords.latitude,
              longitude: coords.longitude,
              orderSum: cartStore.totalPrice,
              productIds,
              comboIds,
            })
            if (data?.branchId && !data.outOfZone) {
              storeInstitution.setSelectedBranchId(Number(data.branchId))
              if (data.zoneId) checkoutFormStore.setField('delivery_zone_id', data.zoneId)
              errorMessage.value = 'Ближайший филиал переполнен — подобрали другой. Нажмите «Оформить» ещё раз.'
            }
          } catch (reResolveError) {
            consola.error('❌ Повторный подбор филиала не удался:', reResolveError)
          }
        }
      }

      throw error // Пробрасываем ошибку для обработки в OrderVerification
    } finally {
      loader.value = false
      setTimeout(() => {
        errorMessage.value = null
        successMessage.value = null
      }, 6000)
    }
  }

  const onValidateS = () => {

    v$.value.$validate()

  }

  const resetForm = () => {
    checkoutFormStore.resetForm()
    v$.value.$reset()
  }

  return {
    v$,
    isSubmitted,
    submitForm,
    resetForm,
    loader,
    errorMessage,
    successMessage,
    deliveryCost,
    paymentOptions,
    confirmationToken,
    currentOrderId,
    onValidateS,
  }
}
